import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ChevronDown } from "lucide-react";
import { BRAND, HERO_IMAGES } from "../data/content";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

    return (
        <section
            id="home"
            ref={ref}
            data-testid="hero-section"
            className="relative h-screen min-h-[720px] w-full overflow-hidden bg-[#0a0908]"
        >
            {/* Background image with parallax */}
            <motion.div
                style={{ y, scale }}
                className="absolute inset-0 z-0"
            >
                <img
                    src={HERO_IMAGES[0]}
                    alt="bride"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908]/55 via-[#0a0908]/55 to-[#0a0908]" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0908]/70 via-transparent to-[#0a0908]/40" />
            </motion.div>

            {/* Decorative gold corners */}
            <div className="absolute top-24 left-6 sm:left-12 w-16 h-16 border-l border-t border-[#d4af37]/50 z-10" />
            <div className="absolute top-24 right-6 sm:right-12 w-16 h-16 border-r border-t border-[#d4af37]/50 z-10" />
            <div className="absolute bottom-24 left-6 sm:left-12 w-16 h-16 border-l border-b border-[#d4af37]/50 z-10" />
            <div className="absolute bottom-24 right-6 sm:right-12 w-16 h-16 border-r border-b border-[#d4af37]/50 z-10" />

            {/* Side label */}
            <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 -rotate-90 origin-left z-20">
                <span className="text-[10px] tracking-[0.5em] uppercase text-[#d4af37]/70">
                    Est. 2017 · Bekasi — Karawang — Jakarta
                </span>
            </div>

            {/* Content */}
            <motion.div
                style={{ opacity }}
                className="relative z-20 h-full flex flex-col items-center justify-center text-center px-6"
            >
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 1 }}
                    className="eyebrow mb-8"
                >
                    Wedding Makeup Atelier
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 1.1 }}
                    className="font-display text-[#fdfbf7] text-[14vw] sm:text-[10vw] lg:text-[8.5vw] leading-[0.95] tracking-[-0.01em] max-w-6xl"
                >
                    <span className="block italic font-serif text-[#d4af37]">
                        Pesona
                    </span>
                    Pengantin
                    <span className="block">Nusantara</span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 1 }}
                    className="mt-8 max-w-xl text-[#a6a196] text-base sm:text-lg leading-relaxed font-light"
                >
                    Mahakarya tata rias yang merangkai tradisi, sentuhan
                    editorial, dan jiwa Indonesia — di hari paling berharga
                    Anda.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.9 }}
                    className="mt-10 flex flex-col sm:flex-row items-center gap-4"
                >
                    <button
                        data-testid="hero-cta-book"
                        onClick={() =>
                            document
                                .getElementById("contact")
                                ?.scrollIntoView({ behavior: "smooth" })
                        }
                        className="group relative bg-[#d4af37] text-[#0a0908] px-10 py-4 text-xs tracking-[0.3em] uppercase font-semibold overflow-hidden"
                    >
                        <span className="relative z-10 transition-colors group-hover:text-[#fdfbf7]">
                            Reservasi Konsultasi
                        </span>
                        <span className="absolute inset-0 bg-[#0a0908] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                    </button>
                    <button
                        data-testid="hero-cta-portfolio"
                        onClick={() =>
                            document
                                .getElementById("portfolio")
                                ?.scrollIntoView({ behavior: "smooth" })
                        }
                        className="border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37]/10 px-10 py-4 text-xs tracking-[0.3em] uppercase transition-all duration-500"
                    >
                        Lihat Karya
                    </button>
                </motion.div>
            </motion.div>

            {/* Bottom marquee */}
            <div className="absolute bottom-0 inset-x-0 z-20 border-t border-[#d4af37]/15 bg-[#0a0908]/40 backdrop-blur-md py-4 overflow-hidden">
                <div className="marquee-track">
                    {[...Array(2)].map((_, j) => (
                        <div
                            key={j}
                            className="flex items-center gap-12 pr-12 whitespace-nowrap"
                        >
                            {[
                                "Akad Adat Jawa",
                                "✦",
                                "Resepsi Modern",
                                "✦",
                                "Editorial Prewedding",
                                "✦",
                                "Hijab Styling",
                                "✦",
                                "Paes & Sanggul",
                                "✦",
                                "Destination Wedding",
                                "✦",
                            ].map((t, i) => (
                                <span
                                    key={i}
                                    className={`font-display text-sm tracking-[0.4em] uppercase ${
                                        t === "✦"
                                            ? "text-[#d4af37]"
                                            : "text-[#fdfbf7]/70"
                                    }`}
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* Scroll indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 1 }}
                className="absolute bottom-20 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2 text-[#d4af37]"
            >
                <span className="text-[10px] tracking-[0.4em] uppercase">
                    Scroll
                </span>
                <ChevronDown size={18} className="animate-bounce" />
            </motion.div>
        </section>
    );
}
